import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ContractsTabsListOption<TValue extends string = string> {
  value: TValue;
  /** i18n key resolved via t(). Use this for translated tab labels. */
  labelKey?: string;
  /** Raw label — rendered as-is when labelKey is omitted (handy for
      dialogs/admin surfaces where the labels aren't translated). */
  label?: string;
  /** Optional adornment rendered next to the label (count badge, icon, …). */
  badge?: React.ReactNode;
}

interface ContractsTabsListProps<TValue extends string = string> {
  options: ReadonlyArray<ContractsTabsListOption<TValue>>;
  /** Currently active tab value. Needed only so the mobile overflow
      dropdown can highlight the selected item and reflect its label in
      the trigger when the active tab lives in the overflow group. The
      inline `TabsTrigger`s pick up the active state from Radix Tabs
      context automatically. */
  activeValue: TValue;
  /** Called when a tab inside the overflow dropdown is chosen. The inline
      `TabsTrigger`s go through Radix's own onValueChange, so this is only
      invoked for the dropdown items. */
  onSelect: (value: TValue) => void;
  /** How many tabs render inline on mobile before the rest collapse into
      a "More" dropdown. Defaults to 3. */
  visibleCount?: number;
  /** i18n key for the overflow trigger fallback label when the active tab
      isn't in the overflow group. */
  moreLabelKey?: string;
}

export function ContractsTabsList<TValue extends string = string>({
  options,
  activeValue,
  onSelect,
  visibleCount = 3,
  moreLabelKey = 'ORGCONTRACTS_TABS_MORE',
}: ContractsTabsListProps<TValue>) {
  const { t } = useTranslation();

  const visibleTabs = options.slice(0, visibleCount);
  const overflowTabs = options.slice(visibleCount);
  const activeOverflowTab = overflowTabs.find((tab) => tab.value === activeValue);

  const labelFor = (tab: ContractsTabsListOption<TValue>) =>
    tab.labelKey ? t(tab.labelKey) : (tab.label ?? '');

  return (
    <>
      {/* Desktop: every tab inline, equal width + centered text. */}
      <TabsList className="hidden md:flex w-max gap-1">
        {options.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value} className="  gap-1.5">
            <span className="truncate">{labelFor(tab)}</span>
            {tab.badge}
          </TabsTrigger>
        ))}
      </TabsList>

      {/* Mobile: first N inline, rest collapsed into a "More" dropdown.
          Each cell (including the More trigger) shares the row equally
          with centered text so the bar reads as a single segmented
          control instead of left-aligned chips. */}
      <TabsList className="md:hidden w-full gap-1">
        {visibleTabs.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value} className="flex-1 justify-center gap-1.5">
            <span className="truncate">{labelFor(tab)}</span>
            {tab.badge}
          </TabsTrigger>
        ))}
        {overflowTabs.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex-1 inline-flex items-center justify-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  activeOverflowTab
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <span className="truncate">
                  {activeOverflowTab ? labelFor(activeOverflowTab) : t(moreLabelKey, 'More')}
                </span>
                {activeOverflowTab?.badge}
                <ChevronDown className="h-4 w-4 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {overflowTabs.map((tab) => (
                <DropdownMenuItem
                  key={tab.value}
                  onClick={() => onSelect(tab.value)}
                  className={cn(
                    'gap-1.5',
                    activeValue === tab.value && 'bg-muted font-semibold text-foreground'
                  )}
                >
                  <span>{labelFor(tab)}</span>
                  {tab.badge}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TabsList>
    </>
  );
}

export default ContractsTabsList;
